import React from "react";
import { Link } from "react-router-dom";
import { useGet } from "../../hooks/useApi.js";

/**
 * Similar tickets — open duplicates and prior closed fixes, ranked by real
 * text evidence (summary, resolution notes, logged work notes) plus category.
 *
 * Replaces the old "AI duplicate detection: no comparable open tickets found"
 * timeline noise: that only looked at open tickets and told you nothing.
 */

interface SimilarTicket {
  ticketId: string;
  cwTicketId: number;
  title: string;
  state: "open" | "closed";
  score: number;
  why: string;
  sharedTerms: string[];
  evidenceSnippet: string | null;
  timeEntryNotes: number;
}

export function SimilarTickets({ ticketId }: { ticketId: string }) {
  const { data, loading } = useGet<{ items: SimilarTicket[] }>(
    ticketId ? `/api/v1/tickets/${ticketId}/similar` : null,
  );
  const items = data?.items ?? [];

  return (
    <section
      aria-label="Similar tickets"
      className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-[hsl(var(--content))]">Similar tickets</h2>
        <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--content-placeholder))]">
          open + closed + logged work
        </span>
      </div>

      {loading ? (
        <div className="mt-3 h-12 animate-pulse rounded-lg bg-[hsl(var(--surface-hover))]" />
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
          No similar tickets found — nothing shared enough with this ticket across open tickets, closed tickets or
          logged time entries.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((t) => (
            <li
              key={`${t.ticketId}-${t.cwTicketId}`}
              data-testid={`similar-${t.cwTicketId}`}
              className="rounded-lg border border-[hsl(var(--border))] p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={
                    t.state === "open"
                      ? "rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      : "rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  }
                >
                  {t.state}
                </span>
                {t.ticketId ? (
                  <Link
                    to={`/tickets/${t.ticketId}`}
                    className="text-sm font-medium text-brand-600 hover:underline"
                  >
                    #{t.cwTicketId}
                  </Link>
                ) : (
                  <span className="text-sm font-medium">#{t.cwTicketId}</span>
                )}
                <span className="min-w-0 flex-1 truncate text-sm text-[hsl(var(--content))]" title={t.title}>
                  {t.title}
                </span>
                <span className="text-[10px] font-mono text-[hsl(var(--content-subtle))]">
                  {Math.round(t.score * 100)}%
                </span>
              </div>
              <p className="mt-1 text-[11px] text-[hsl(var(--content-subtle))]">{t.why}</p>
              {t.evidenceSnippet && (
                <p className="mt-1 line-clamp-2 text-xs italic text-[hsl(var(--content-subtle))]">
                  “{t.evidenceSnippet}”
                  {t.timeEntryNotes > 0 && (
                    <span className="not-italic"> · {t.timeEntryNotes} logged work note(s)</span>
                  )}
                </p>
              )}
              {!t.evidenceSnippet && t.timeEntryNotes > 0 && (
                <p className="mt-1 text-[11px] text-[hsl(var(--content-subtle))]">
                  Matched on {t.timeEntryNotes} logged work note(s).
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
